import { useCallback, useEffect, useRef, useState } from "react";
import type { Invite, Uuid } from "@shared/api";
import { Tile, type Person } from "@entities/user";
import { Popover, PopoverAnchor, PopoverContent } from "@shared/ui/popover";
import { IconChevronRight, IconClose, IconInvite, IconMembers, IconVoiceChannel } from "@shared/ui/icons";
import { ownLink, sendLink } from "../lib/link";

/** Кого можно позвать: участник сервера, которого нет в этом звонке. */
export interface InviteCandidate {
  id: Uuid;
  person: Person;
  /** В каком голосовом канале он сейчас — имя; не в голосе — `null`. */
  voice: string | null;
}

/** Срок строки — как в Discord: десять секунд, пока её не трогают. */
const LIFETIME = 10_000;
/** Сколько людей в подсказке — как в Discord. */
const SUGGESTED = 5;
/** Сколько ждать, прежде чем закрыть подсказку, когда указатель ушёл и с строки, и с неё. */
const LINGER = 150;

type Sent = "busy" | "sent";

/** «Меньше движения» — в своих настройках или в системе. */
function still(): boolean {
  return (
    document.documentElement.hasAttribute("data-reduce-motion") ||
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true
  );
}

/**
 * «Пригласить в голосовой чат» — строка под своим голосовым каналом, как в
 * Discord (`VoiceInviteSuggestions`). Видна, только пока человек в этом звонке
 * и вправе приглашать; вошёл снова — видна снова.
 *
 * Живёт десять секунд, кольцо вокруг значка их отсчитывает. Пока строку
 * трогают (указатель, фокус, открытая подсказка), кольцо стоит полным, а
 * отпустили — отсчёт начинается заново. С «меньше движения» срока нет вовсе:
 * строка ждёт, пока её закроют крестиком.
 *
 * Наведение открывает подсказку справа — до пяти человек, «Пригласить» одним
 * щелчком (ссылка на канал уходит в личку), и «Показать больше». Щелчок по
 * самой строке — сразу окно приглашения.
 */
export function VoiceInviteRow({
  serverId,
  meId,
  channelId,
  candidates,
  onShowMore,
  onClose,
}: {
  serverId: Uuid;
  meId: Uuid;
  channelId: Uuid;
  /** Уже по порядку: кого звать вероятнее — выше. */
  candidates: InviteCandidate[];
  onShowMore: () => void;
  onClose: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [open, setOpen] = useState(false);
  /** Открыта стрелкой, а не наведением: фокус уходит внутрь, и наведение её не закрывает. */
  const [forced, setForced] = useState(false);
  const [leaving, setLeaving] = useState(false);
  /** Сколько раз начинался отсчёт: новое кольцо — заново полное. */
  const [round, setRound] = useState(0);
  const linger = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const row = useRef<HTMLDivElement>(null);
  const motionless = still();
  const held = hovered || focused || open;

  /*
   * Список каналов перерисовывается на каждый чих звонка и отдаёт новый
   * `onClose`: в зависимостях отсчёта он сбрасывал бы десять секунд заново.
   */
  const close = useRef(onClose);
  useEffect(() => {
    close.current = onClose;
  });

  const leave = useCallback(() => {
    setOpen(false);
    if (motionless) return close.current();
    setLeaving(true);
    setTimeout(() => close.current(), 200);
  }, [motionless]);

  useEffect(() => {
    if (held || motionless || leaving) return;
    setRound((value) => value + 1);
    const timer = setTimeout(leave, LIFETIME);
    return () => clearTimeout(timer);
  }, [held, motionless, leaving, leave]);

  useEffect(() => () => clearTimeout(linger.current), []);

  /** Наведение — открыть; ушли и со строки, и с подсказки — закрыть, чуть погодя. */
  function hover(inside: boolean) {
    clearTimeout(linger.current);
    if (inside) {
      setOpen(true);
      return;
    }
    if (forced) return;
    linger.current = setTimeout(() => setOpen(false), LINGER);
  }

  return (
    <div className={`vinv-wrap${leaving ? " leaving" : ""}${motionless ? " still" : ""}`}>
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setForced(false);
        }}
      >
        <PopoverAnchor asChild>
          <div
            ref={row}
            role="button"
            tabIndex={0}
            className="voice-live-row vinv-row"
            aria-label="Пригласить в голосовой чат"
            onClick={onShowMore}
            onKeyDown={(event) => {
              if (event.key !== "Enter" && event.key !== " ") return;
              event.preventDefault();
              onShowMore();
            }}
            onPointerEnter={() => {
              setHovered(true);
              hover(true);
            }}
            onPointerLeave={() => {
              setHovered(false);
              hover(false);
            }}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
          >
            <span className="vinv-lead">
              {!motionless && (
                <svg key={round} className={`vinv-timer${held ? " paused" : ""}`} viewBox="0 0 24 24" aria-hidden>
                  <circle className="vinv-ring" cx="12" cy="12" r="11" />
                  <circle className="vinv-progress" cx="12" cy="12" r="11" pathLength={100} />
                </svg>
              )}
              <IconInvite size={14} />
            </span>
            <span className="ellipsis grow vinv-label">Пригласить в голосовой чат</span>
            {hovered ? (
              <button
                type="button"
                className="vinv-end"
                aria-label="Закрыть"
                onClick={(event) => {
                  event.stopPropagation();
                  leave();
                }}
              >
                <IconClose size={14} />
              </button>
            ) : (
              <button
                type="button"
                className="vinv-end"
                aria-label="Кого можно пригласить в голосовой чат"
                onClick={(event) => {
                  event.stopPropagation();
                  setForced(true);
                  setOpen(true);
                }}
              >
                <IconChevronRight size={14} />
              </button>
            )}
          </div>
        </PopoverAnchor>
        <PopoverContent
          side="right"
          align="start"
          sideOffset={17}
          aria-label="Кого можно пригласить в голосовой чат"
          onOpenAutoFocus={(event) => {
            if (!forced) event.preventDefault();
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (forced) row.current?.focus();
          }}
          onPointerEnter={() => hover(true)}
          onPointerLeave={() => hover(false)}
        >
          <Suggestions
            serverId={serverId}
            meId={meId}
            channelId={channelId}
            candidates={candidates.slice(0, SUGGESTED)}
            onShowMore={() => {
              setOpen(false);
              onShowMore();
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

/** Подсказка: до пяти человек с «Пригласить» и «Показать больше». */
function Suggestions({
  serverId,
  meId,
  channelId,
  candidates,
  onShowMore,
}: {
  serverId: Uuid;
  meId: Uuid;
  channelId: Uuid;
  candidates: InviteCandidate[];
  onShowMore: () => void;
}) {
  const [sent, setSent] = useState<Record<Uuid, Sent>>({});
  /** Ссылка одна на всю подсказку: каждое «Пригласить» не должно заводить новую. */
  const link = useRef<Promise<Invite> | null>(null);

  async function invite(id: Uuid) {
    if (sent[id]) return;
    setSent((previous) => ({ ...previous, [id]: "busy" }));
    try {
      link.current ??= ownLink(serverId, meId, channelId);
      const { code } = await link.current.catch((error: unknown) => {
        link.current = null;
        throw error;
      });
      await sendLink(id, code);
      setSent((previous) => ({ ...previous, [id]: "sent" }));
    } catch {
      setSent((previous) => {
        const next = { ...previous };
        delete next[id];
        return next;
      });
    }
  }

  return (
    <div className="vinv-pop surface-pop">
      <ul className="vinv-list">
        {candidates.map((candidate) => {
          const state = sent[candidate.id];
          return (
            <li key={candidate.id}>
              <button
                type="button"
                className="vinv-item"
                aria-label="Пригласить"
                aria-disabled={!!state}
                onClick={() => void invite(candidate.id)}
              >
                <Tile person={candidate.person} size={24} />
                <span className="vinv-who">
                  <span className="ellipsis vinv-name">{candidate.person.name}</span>
                  {candidate.voice && (
                    <span className="vinv-where">
                      <IconVoiceChannel size={12} />
                      <span className="ellipsis">{candidate.voice}</span>
                    </span>
                  )}
                </span>
                {state === "busy" ? (
                  <span className="vinv-dots" aria-label="Отправляется">
                    <i />
                    <i />
                    <i />
                  </span>
                ) : state === "sent" ? (
                  <span className="vinv-sent">Отправлено</span>
                ) : (
                  <span className="vinv-act">
                    <IconInvite size={16} />
                  </span>
                )}
              </button>
            </li>
          );
        })}
        <li>
          <button type="button" className="vinv-item" onClick={onShowMore}>
            <span className="vinv-lead-more">
              <IconMembers size={18} />
            </span>
            <span className="ellipsis vinv-name">Показать больше</span>
          </button>
        </li>
      </ul>
    </div>
  );
}
